import {
  Button,
  Icon,
  IconSizeClasses,
  Input,
  Tooltip,
} from '@eppendorf/vnls-react-components';
import * as Dialog from '@radix-ui/react-dialog';
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useSyncExternalStore,
} from 'react';

import './chat-bot.scss';
import i18next from '$shared/i18n/i18n';

import { MessageList } from './message-list';
import { useChatBot } from './use-chat-bot';

interface ChatDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const MIN_WIDTH = 320;
const MIN_HEIGHT = 400;
const DEFAULT_WIDTH = 449;
const DEFAULT_HEIGHT = 600;
const MOBILE_BREAKPOINT =
  '(max-width: 599px), (max-height: 499px) and (orientation: landscape)';

function useIsMobile() {
  const subscribe = useCallback((callback: () => void) => {
    const mql = window.matchMedia(MOBILE_BREAKPOINT);
    mql.addEventListener('change', callback);
    return () => mql.removeEventListener('change', callback);
  }, []);
  const getSnapshot = useCallback(() => window.matchMedia(MOBILE_BREAKPOINT).matches, []);
  return useSyncExternalStore(subscribe, getSnapshot);
}

export function ChatDialog({ isOpen, onClose }: ChatDialogProps) {
  const [inputValue, setInputValue] = useState('');
  const { messages, isLoading, sendMessage, giveFeedback } = useChatBot();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dimensions, setDimensions] = useState({
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
  });
  const isMobile = useIsMobile();
  const [isFullView, setIsFullView] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const resizingRef = useRef<{
    direction: string;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
  } | null>(null);

  // Suppress tooltips during the opening animation to prevent
  // the "Full view" hint from flashing when the dialog scales in.
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const id = setTimeout(() => setIsAnimating(false), 600); // matches $chat-duration
      return () => clearTimeout(id);
    }
    setIsAnimating(false);
    return undefined;
  }, [isOpen]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // Scroll to bottom when messages are added
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Scroll to bottom immediately when chat is reopened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => scrollToBottom('instant'), 50);
    }
  }, [isOpen, scrollToBottom]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    await sendMessage(inputValue);
    setInputValue('');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const startResize = useCallback(
    (e: React.MouseEvent, direction: string) => {
      if (isFullView) return;
      e.preventDefault();
      setIsResizing(true);
      resizingRef.current = {
        direction,
        startX: e.clientX,
        startY: e.clientY,
        startWidth: dimensions.width,
        startHeight: dimensions.height,
      };

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!resizingRef.current) return;
        const {
          direction: dir,
          startX,
          startY,
          startWidth,
          startHeight,
        } = resizingRef.current;
        let newWidth = startWidth;
        let newHeight = startHeight;

        if (dir === 'left' || dir === 'corner') {
          newWidth = Math.max(
            MIN_WIDTH,
            Math.min(startWidth + (startX - moveEvent.clientX), window.innerWidth - 32),
          );
        }
        if (dir === 'top' || dir === 'corner') {
          newHeight = Math.max(MIN_HEIGHT, startHeight + (startY - moveEvent.clientY));
        }
        setDimensions({ width: newWidth, height: newHeight });
      };

      const onMouseUp = () => {
        resizingRef.current = null;
        setIsResizing(false);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);
      };

      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    },
    [dimensions, isFullView],
  );

  const effectiveFullView = isFullView || isMobile;

  const dialogStyle = effectiveFullView
    ? {}
    : {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
      };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 chat-dialog-overlay" />
        <Dialog.Content
          className={`fixed flex flex__dir--column bg-white chat-dialog-content${effectiveFullView ? ' chat-dialog-content--fullview' : ''}${isResizing ? ' chat-dialog-content--resizing' : ''}${isMobile ? ' chat-dialog-content--mobile' : ''}`}
          style={dialogStyle}
        >
          {/* Invisible resize areas (hidden in full-view and mobile) */}
          {!effectiveFullView && (
            <>
              <div
                role="presentation"
                aria-hidden="true"
                onMouseDown={(e) => startResize(e, 'corner')}
                className="chat-resize-area chat-resize-area--corner"
              />
              <div
                role="presentation"
                aria-hidden="true"
                onMouseDown={(e) => startResize(e, 'left')}
                className="chat-resize-area chat-resize-area--left"
              />
              <div
                role="presentation"
                aria-hidden="true"
                onMouseDown={(e) => startResize(e, 'top')}
                className="chat-resize-area chat-resize-area--top"
              />
              {/* Visible left resize handle (indicator) */}
              <div
                role="presentation"
                aria-hidden="true"
                onMouseDown={(e) => startResize(e, 'left')}
                className="chat-resize-handle-left"
              >
                <Icon name="left" size={IconSizeClasses.XSmall} className="bg-gray-700" />
              </div>
            </>
          )}

          {/* Header */}
          <div className="flex flex__ai--center bg-blue-700 chat-header">
            <Dialog.Title className="font-size-l font-weight-regular color-white flex-grow">
              {i18next.t('translation:chatbot.title')}
            </Dialog.Title>
            {isMobile ? (
              <button
                type="button"
                className="chat-header-btn"
                onClick={onClose}
                aria-label={i18next.t(
                  'translation:chatbot.closeTitle',
                  'Close assistant',
                )}
              >
                <Icon
                  name="close"
                  size={IconSizeClasses.Small}
                  className="chat-icon--white"
                />
              </button>
            ) : (
              <Tooltip
                isOpen={isAnimating ? false : undefined}
                trigger={
                  <button
                    type="button"
                    className="chat-header-btn"
                    onClick={() => setIsFullView((v) => !v)}
                  >
                    <Icon
                      name={
                        isFullView ? 'chat-full-view-collapse' : 'chat-full-view-expand'
                      }
                      size={IconSizeClasses.XSmall}
                      className="chat-icon--white"
                    />
                  </button>
                }
              >
                {isFullView
                  ? i18next.t('translation:chatbot.exitFullView', 'Exit full view')
                  : i18next.t('translation:chatbot.fullView', 'Full view')}
              </Tooltip>
            )}
          </div>

          {/* Chat Content */}
          <div className="flex flex__dir--column flex-grow chat-content">
            {/* Messages Container */}
            <div className="chat-messages">
              <MessageList
                messages={messages}
                isLoading={isLoading}
                onFeedback={giveFeedback}
              />
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="flex gap-s chat-input-area">
              <Input
                id="chat-message-input"
                className="flex-grow"
                placeholder={i18next.t('translation:chatbot.messagePlaceholder')}
                value={inputValue}
                onChange={(
                  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                ) => setInputValue(e.target.value)}
                onKeyDownCapture={handleKeyDown}
                disabled={isLoading}
                ref={inputRef}
              />
              <Button
                id="chatbot-send-button"
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                variant="primary"
                size="small"
                className="flex flex__ai--center p-s chat-send-button"
              >
                <Icon
                  name="send"
                  size={IconSizeClasses.XSmall}
                  className={!inputValue.trim() || isLoading ? 'bg-gray-500' : 'bg-white'}
                />
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Re-export the type for backward compatibility
export type { ChatMessage } from './types';
